import React from 'react';
import { ArrowLeftIcon, VideoIcon, PhoneIcon, MoreVerticalIcon, SmileIcon, PaperclipIcon, CameraIcon, MicIcon, CheckCheckIcon, SignalHighIcon, WifiIcon, BatteryMediumIcon } from 'lucide-react';
import type { ChatThread } from '../data/chats';

/**
 * Pixel-crisp recreation of a WhatsApp conversation, rendered as real markup
 * so it stays sharp at any card size and on retina screens.
 */
export function WhatsAppChat({
  thread
}: {
  thread: ChatThread;
}) {
  return <div className="flex h-full flex-col bg-[#ece5dd] font-sans" data-id="element-150" data-mp="1afvd3wh60z56">
      {/* status bar */}
      <div className="flex items-center justify-between bg-[#075e54] px-3 pb-1 pt-1.5 text-[10px] font-medium text-white/95" data-id="element-151" data-mp="15h1w3u2o6q09">
        <span data-id="element-152" data-mp="10j8ttc4hmg59">{thread.statusTime}</span>
        <span className="flex items-center gap-1" data-id="element-153" data-mp="10kbjxhtzkek4">
          <SignalHighIcon className="h-3 w-3" aria-hidden="true" data-id="element-154" data-mp="2zvl1c4elj7lz" />
          <WifiIcon className="h-3 w-3" aria-hidden="true" data-id="element-155" data-mp="2uw8dbgbznuht" />
          <span data-id="element-156" data-mp="2uwrkq340j0hl">{thread.battery}</span>
          <BatteryMediumIcon className="h-3 w-3" aria-hidden="true" data-id="element-157" data-mp="cbcp4gbc19hr" />
        </span>
      </div>

      {/* conversation header */}
      <div className="flex items-center gap-2 bg-[#075e54] px-3 pb-2.5 text-white" data-id="element-158" data-mp="gd6j65r0vf8y">
        <ArrowLeftIcon className="h-4 w-4 shrink-0" aria-hidden="true" data-id="element-159" data-mp="2doqvgeipjuu" />
        <img src={thread.avatar} alt={`Foto de perfil de ${thread.contact}`} width={64} height={64} loading="lazy" decoding="async" className="h-8 w-8 shrink-0 rounded-full bg-[#0c7a6c] object-cover" data-id="element-160" data-mp="2fhxwwt1m63d" />
        <span className="min-w-0 flex-1 leading-tight" data-id="element-161" data-mp="1004dewlsx7p9">
          <span className="block truncate text-sm font-semibold" data-id="element-162" data-mp="lcsyckqev5ke">
            {thread.contact}
          </span>
          <span className="block text-[10px] text-white/75" data-id="element-163" data-mp="q9t5z02wsgdm">Online</span>
        </span>
        <VideoIcon className="h-4 w-4 shrink-0" aria-hidden="true" data-id="element-164" data-mp="1e0f521uxqqbh" />
        <PhoneIcon className="h-4 w-4 shrink-0" aria-hidden="true" data-id="element-165" data-mp="1irf174w8d4cx" />
        <MoreVerticalIcon className="h-4 w-4 shrink-0" aria-hidden="true" data-id="element-166" data-mp="1isq4l3lrka3i" />
      </div>

      {/* messages */}
      <div className="flex-1 space-y-2 px-3 py-3" data-id="element-167" data-mp="1sl1btbwngw9g">
        <p className="mx-auto w-fit rounded bg-[#d9e4ea] px-2.5 py-1 text-[10px] font-medium text-[#4a5a60]" data-id="element-168" data-mp="1npc8cxhiev7v">
          {thread.date}
        </p>

        {thread.messages.map((message, i) => {
        const outgoing = message.from === 'master';
        return <div key={i} className={`flex ${outgoing ? 'justify-end' : 'justify-start'}`} data-id="element-169" data-mp="19o9liiioxgvw" data-mpi={i}>
              <div className={`max-w-[82%] rounded-lg px-2 pb-1.5 pt-1.5 text-[12px] leading-snug text-[#111b21] shadow-[0_1px_1px_rgba(0,0,0,0.12)] ${outgoing ? 'bg-[#dcf8c6]' : 'bg-white'}`} data-id="element-170" data-mp="14wrryfizyiky">
                {message.image ? <img src={message.image} alt={message.imageAlt} width={1200} height={900} loading="lazy" decoding="async" className="mb-1 aspect-[4/3] w-full rounded object-cover" data-id="element-171" data-mp="qx0csbgquw5d" /> : null}
                {message.text ? <span className="px-0.5" data-id="element-172" data-mp="36oa5hkb3fwys">{message.text}</span> : null}
                <span className="mt-0.5 flex items-center justify-end gap-1 text-[9px] text-[#667781]" data-id="element-173" data-mp="3bl0ks5f2qoj5">
                  {message.time}
                  {outgoing && <CheckCheckIcon className="h-3 w-3 text-[#34b7f1]" aria-hidden="true" data-id="element-174" data-mp="2ix4p9418r2ny" />}
                </span>
              </div>
            </div>;
      })}
      </div>

      {/* input bar */}
      <div className="flex items-center gap-2 px-2 pb-2.5 pt-1" data-id="element-175" data-mp="1v7tk7acuym40">
        <div className="flex flex-1 items-center gap-2 rounded-full bg-white px-3 py-2 text-[#8696a0]" data-id="element-176" data-mp="1v7jttlnwicxs">
          <SmileIcon className="h-4 w-4" aria-hidden="true" data-id="element-177" data-mp="2053mhqlcis99" />
          <span className="flex-1 text-[11px]" data-id="element-178" data-mp="6p8ykf2s4ns7">Message</span>
          <PaperclipIcon className="h-4 w-4" aria-hidden="true" data-id="element-179" data-mp="1p330nte2h4u" />
          <CameraIcon className="h-4 w-4" aria-hidden="true" data-id="element-180" data-mp="1qnug4kepjyh" />
        </div>
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#0c7a6c]" data-id="element-181" data-mp="fhj7lt1xun7v">
          <MicIcon className="h-4 w-4 text-white" aria-hidden="true" data-id="element-182" data-mp="aozjw39jebpc" />
        </span>
      </div>
    </div>;
}